import axios from "axios";
import { baseMvteApi } from "src/config";
import { OrderDispatch, OrderImage } from "src/types";
import { OrderNote } from "src/types/order-note";
import { BillingComment } from "src/types/billing-comment";
import { msalInstance } from "src/lib/msal";
import { UnbilledOrder } from "src/types/unbilled-order";

export const mvteApiInstance = axios.create({
    baseURL: baseMvteApi,
});

//Authentication
mvteApiInstance.interceptors.request.use(
    async (config) => {
        const account = msalInstance.getAllAccounts()[0];
        if (account) {
            const { accessToken } = await msalInstance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: account,
            });

            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

export async function getUnbilledOrders({
    queueName,
    orderStatuses,
}: {
    queueName: string;
    orderStatuses?: string[];
}) {
    const { data } = await mvteApiInstance.get<UnbilledOrder[]>(
        "/unbilled-orders",
        {
            params: {
                queueName,
                orderStatuses: orderStatuses?.join(","),
            },
        },
    );

    return data;
}

//Order Notes
type GetOrderNotes = {
    status: "success" | "error" | "fail";
    data: {
        orderNotes: OrderNote[];
    };
    message: string;
};

export async function fetchOrderNotes({
    orderNumber,
}: {
    orderNumber: string | null;
}) {
    const { data } = await mvteApiInstance.get<GetOrderNotes>(
        `/orders/${orderNumber}/notes`,
    );

    if (data.status === "error") {
        throw new Error(data.message);
    }

    return data.data.orderNotes;
}

//Billing Comments
type GetBillingComments = {
    status: "success" | "error" | "fail";
    data: {
        customerBillingComments: BillingComment;
    };
    message: string;
};

export async function fetchBillingComments({
    customerCode,
}: {
    customerCode: string | null;
}) {
    const { data } = await mvteApiInstance.get<GetBillingComments>(
        `customers/${customerCode}/billing-comments`,
    );

    if (data.status === "error") {
        throw new Error(data.message);
    }

    return data.data.customerBillingComments;
}

//Order Dispatches
type GetOrderDispatches = {
    status: "success" | "error" | "fail";
    data: {
        orderDispatches: OrderDispatch[];
    };
    message: string;
};

export async function fetchOrderDispatches({
    orderNumber,
}: {
    orderNumber: string | null;
}) {
    const { data } = await mvteApiInstance.get<GetOrderDispatches>(
        `/orders/${orderNumber}/dispatches`,
    );

    if (data.status === "error") {
        throw new Error(data.message);
    }

    return data.data.orderDispatches;
}

type FetchOrderImages = {
    status: "success" | "error" | "fail";
    data: {
        orderImages: OrderImage[];
    };
    message: string;
};

export async function fetchOrderImages({
    orderNumber,
}: {
    orderNumber: string;
}) {
    const { data } = await mvteApiInstance.get<FetchOrderImages>(
        `/orders/${orderNumber}/images`,
    );

    return data.data.orderImages;
}

export function postOrderNote(data: {
    EmployeeCode: string;
    NoteText: string;
    NoteType: string;
    Payload: {
        CustomerCode: string;
        OrderNumber: string;
    };
    UserPrincipalName: string;
}) {
    return mvteApiInstance.post(`/orders/${data.Payload.OrderNumber}/notes`, {
        ...data,
        Payload: JSON.stringify(data.Payload),
    });
}

export function postOrderDocument(data: {
    AppSource: string;
    AppVersion: string;
    CreatedBy: string;
    DispatchNumber: string;
    DocumentType: string;
    DriverCode: string;
    FileData: number[];
    FileName: string;
    OrderNumber: string;
}) {
    return mvteApiInstance.post(`orders/${data.OrderNumber}/images`, data);
}

export function postEnqueueOrder(data: {
    OrderNumber: string;
    ToQueue: string;
    CurrentQueue: string;
    EmployeeCode: string;
    Payload: {
        CustomerCode: string;
        OrderNumber: string;
    };
    UserPrincipalName: string;
}) {
    return mvteApiInstance.post(
        `/unbilled-orders/${data.OrderNumber}/enqueue`,
        {
            ...data,
            Payload: JSON.stringify(data.Payload),
        },
    );
}

export function postCompleteEnqueuedOrder(data: {
    OrderNumber: string;
    CurrentQueue: string;
    EmployeeCode: string;
    Payload: {
        CustomerCode: string;
        OrderNumber: string;
    };
    UserPrincipalName: string;
}) {
    return mvteApiInstance.post(
        `/unbilled-orders/${data.OrderNumber}/complete-queue`,
        {
            ...data,
            Payload: JSON.stringify(data.Payload),
        },
    );
}
